// =============================================================================
// AppInstalls — super-admin PWA install board.
//
// Global install metrics + a per-tenant breakdown. Reads the App DB's pwa_*
// profile columns via the pwa-install-stats edge function (?breakdown=1), and
// resolves tenant names from the Platform DB's own tenant list. Degrades to a
// quiet notice if the App DB is unreachable or migration 20261114 isn't applied.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import {
  fetchPwaInstallStats, fetchTenants,
  type PwaInstallStats, type PwaInstallTenantRow,
} from '@/lib/api';
import type { Tenant } from '@/types';

const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

function StatTile({ label, value, sub, tone = 'default' }: {
  label: string; value: string; sub: string; tone?: 'default' | 'emerald' | 'amber' | 'muted';
}) {
  const valCol = tone === 'emerald' ? 'text-emerald-600'
    : tone === 'amber' ? 'text-amber-600'
    : tone === 'muted' ? 'text-surface-400'
    : 'text-surface-900';
  return (
    <div className="bg-surface-50 rounded-lg p-3">
      <p className="text-xs text-surface-500 mb-1">{label}</p>
      <p className={`text-xl font-medium ${valCol}`}>{value}</p>
      <p className="text-[11px] text-surface-400 mt-0.5">{sub}</p>
    </div>
  );
}

export function AppInstallsPage() {
  const [stats, setStats] = useState<PwaInstallStats | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [s, t] = await Promise.all([
      fetchPwaInstallStats({ breakdown: true }),
      fetchTenants().catch(() => [] as Tenant[]),
    ]);
    setStats(s); setTenants(t); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants) m.set(t.id, t.name || t.id.slice(0, 8));
    return (id: string) => m.get(id) || id.slice(0, 8);
  }, [tenants]);

  // Per-tenant rows, highest-reach first (most users at top).
  const rows = useMemo(() => {
    const list = (stats?.tenants ?? []).slice();
    list.sort((a, b) => b.total - a.total);
    return list;
  }, [stats]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-surface-900">App installs</h1>
          <p className="text-sm text-surface-500">How many users added the PWA to a device, per tenant, and how the install nudge is converting. Excludes anonymous guests.</p>
        </div>
        <button onClick={load} disabled={loading} title="Refresh"
          className="inline-flex items-center justify-center rounded-lg border border-surface-200 p-2 text-surface-500 hover:bg-surface-50 hover:text-surface-900 disabled:opacity-50">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Spinner /></div>
      ) : !stats ? (
        <p className="text-sm text-surface-400">Install stats unavailable — App DB unreachable, or migration 20261114 not yet applied.</p>
      ) : (
        <>
          {/* Global totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatTile label="Install rate" tone="emerald"
              value={`${pct(stats.installed, stats.total)}%`}
              sub={`${stats.installed.toLocaleString()} of ${stats.total.toLocaleString()} users`} />
            <StatTile label="Active installs · 30d"
              value={stats.activeStandalone30d.toLocaleString()}
              sub="opened the installed app" />
            <StatTile label="WhatsApp install rate"
              value={`${pct(stats.whatsappInstalled, stats.whatsappTotal)}%`}
              sub={`${stats.whatsappInstalled.toLocaleString()} of ${stats.whatsappTotal.toLocaleString()} WhatsApp users`} />
            <StatTile label="Nudged, not installed"
              tone={stats.nudgedCapped > 0 ? 'amber' : 'muted'}
              value={stats.nudgedCapped.toLocaleString()}
              sub={`hit 2-send cap · ${stats.nudgedAny.toLocaleString()} nudged total`} />
          </div>

          {/* Per-tenant breakdown */}
          <h2 className="text-sm font-semibold text-surface-900 mb-2">By tenant</h2>
          {rows.length === 0 ? (
            <p className="text-xs text-surface-400">No per-tenant data yet.</p>
          ) : (
            <div className="overflow-x-auto border border-surface-200 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-surface-500 border-b border-surface-200 bg-surface-50">
                    <th className="px-3 py-2 font-medium">Tenant</th>
                    <th className="px-3 py-2 font-medium text-right">Users</th>
                    <th className="px-3 py-2 font-medium text-right">Installed</th>
                    <th className="px-3 py-2 font-medium text-right">Install rate</th>
                    <th className="px-3 py-2 font-medium text-right">Active 30d</th>
                    <th className="px-3 py-2 font-medium text-right">WA rate</th>
                    <th className="px-3 py-2 font-medium text-right">Nudged, not inst.</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: PwaInstallTenantRow) => {
                    const rate = pct(r.installed, r.total);
                    const rateCol = rate >= 50 ? 'text-emerald-600' : rate >= 20 ? 'text-surface-900' : 'text-amber-600';
                    return (
                      <tr key={r.tenant_id} className="border-b border-surface-100 last:border-0">
                        <td className="px-3 py-2 text-surface-900">{nameOf(r.tenant_id)}</td>
                        <td className="px-3 py-2 text-right text-surface-600">{r.total.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-surface-600">{r.installed.toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right font-medium ${rateCol}`}>{rate}%</td>
                        <td className="px-3 py-2 text-right text-surface-600">{r.activeStandalone30d.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-surface-600">{pct(r.whatsappInstalled, r.whatsappTotal)}%</td>
                        <td className={`px-3 py-2 text-right ${r.nudgedCapped > 0 ? 'text-amber-600' : 'text-surface-400'}`}>{r.nudgedCapped.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
